import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Button,
    Select,
    TextField,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    OutlinedInput,
    Divider,
    InputAdornment,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/system';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { validateInputValue } from 'utils/commonHelpers';
import { timeIntervals, availableOptions } from 'utils/constants';

const EditSchemeDialog = ({ onClose, data, index }) => {
    const theme = useTheme();
    const [disabled, setDisabled] = useState(false);
    const [name, setName] = useState(data.schemeName);
    const [description, setDescription] = useState(data.schemeDescription);
    const [days, setDays] = useState({
        sunday: data.days['sunday'],
        monday: data.days['monday'],
        tuesday: data.days['tuesday'],
        wednesday: data.days['wednesday'],
        thursday: data.days['thursday'],
        friday: data.days['friday'],
        saturday: data.days['saturday']
    });
    const [startTime, setStartTime] = useState(data.startTime);
    const [endTime, setEndTime] = useState(data.endTime);
    const [advancedSettings, setAdvancedSettings] = useState({
        displayInactivityTimeoutAC: data.advancedSettings.displayInactivityTimeoutAC,
        displayInactivityTimeoutDC: data.advancedSettings.displayInactivityTimeoutDC,
        cpuInactivityTimeoutAC: data.advancedSettings.cpuInactivityTimeoutAC,
        cpuInactivityTimeoutDC: data.advancedSettings.cpuInactivityTimeoutDC,
        usbSleepAC: data.advancedSettings.usbSleepAC,
        usbSleepDC: data.advancedSettings.usbSleepDC,
        wakeTimersAC: data.advancedSettings.wakeTimersAC,
        wakeTimersDC: data.advancedSettings.wakeTimersDC,
        hidWake: data.advancedSettings.hidWake,
        cpuMax: data.advancedSettings.cpuMax,
        diskMax: data.advancedSettings.diskMax,
        networkMax: data.advancedSettings.networkMax,
        screenDim: data.advancedSettings.screenDim,
        brightness: data.advancedSettings.brightness
    });

    useEffect(() => {
        if (data.schemeName === 'Background Scheme') {
            setDisabled(true);
        }
    }, []);

    const handleChangeDays = (e) => {
        setDays({
            ...days,
            [e.target.name]: e.target.checked
        });
    };

    const handleChangeAdvancedSettings = (e) => {
        setAdvancedSettings({
            ...advancedSettings,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = () => {
        if (
            validateInputValue(advancedSettings.cpuMax, 'percentage') !== true ||
            validateInputValue(advancedSettings.diskMax, 'percentage') !== true ||
            validateInputValue(advancedSettings.screenDim, 'percentage') !== true
        ) {
            toast.error('Percentage value should be between 0 and 100');
            return true;
        }
        if (validateInputValue(advancedSettings.brightness, 'brightness') !== true) {
            toast.error('It should be between 0 and 255');
            return true;
        }
        if (endTime < startTime) {
            toast.error('End time should be greater than start time');
            return true;
        }
        if (
            days.sunday === false &&
            days.monday === false &&
            days.tuesday === false &&
            days.wednesday === false &&
            days.thursday === false &&
            days.friday === false &&
            days.saturday === false
        ) {
            toast.error('Please select the days');
            return true;
        }
        const updatedSchemeData = {
            id: data?.id,
            schemeName: name,
            schemeDescription: description,
            days: days,
            startTime: startTime,
            endTime: endTime,
            advancedSettings: advancedSettings
        };
        const schemeData = JSON.parse(localStorage.getItem('policySchemeData'));
        schemeData[index] = updatedSchemeData;
        localStorage.setItem('policySchemeData', JSON.stringify(schemeData));
        onClose();
    };

    return (
        <Dialog open={true} onClose={onClose}>
            <DialogTitle sx={{ fontSize: '16px' }}>Edit Scheme</DialogTitle>
            <DialogContent sx={{ minWidth: '500px' }}>
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                sx={{ width: '100%' }}
                                label="Scheme Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                sx={{ width: '100%' }}
                                label="Scheme Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <FormGroup aria-label="position" row>
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="sunday" checked={days.sunday} onChange={handleChangeDays} />}
                            label="Sun"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="monday" checked={days.monday} onChange={handleChangeDays} />}
                            label="Mon"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="tuesday" checked={days.tuesday} onChange={handleChangeDays} />}
                            label="Tue"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="wednesday" checked={days.wednesday} onChange={handleChangeDays} />}
                            label="Wed"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="thursday" checked={days.thursday} onChange={handleChangeDays} />}
                            label="Thr"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="friday" checked={days.friday} onChange={handleChangeDays} />}
                            label="Fri"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                        <FormControlLabel
                            value="top"
                            control={<Checkbox name="saturday" checked={days.saturday} onChange={handleChangeDays} />}
                            label="Sat"
                            labelPlacement="top"
                            disabled={disabled}
                        />
                    </FormGroup>
                </Box>
                <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography>Start Time</Typography>
                            <input
                                type="time"
                                style={{ width: '100%', padding: 3, fontSize: '16px' }}
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                disabled={disabled}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Typography>End Time</Typography>
                            <input
                                type="time"
                                style={{ width: '100%', padding: 3, fontSize: '16px' }}
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                disabled={disabled}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ border: `1px solid ${theme.palette.primary.main}` }} />
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>Display Inactivity Timeout (AC)</InputLabel>
                                <Select
                                    label="Display Inactivity Timeout (AC)"
                                    name="displayInactivityTimeoutAC"
                                    value={advancedSettings.displayInactivityTimeoutAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>Display Inactivity Timeout (DC)</InputLabel>
                                <Select
                                    label="Display Inactivity Timeout (DC)"
                                    name="displayInactivityTimeoutDC"
                                    value={advancedSettings.displayInactivityTimeoutDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>CPU Inactivity Timeout (AC)</InputLabel>
                                <Select
                                    label="CPU Inactivity Timeout (AC)"
                                    name="cpuInactivityTimeoutAC"
                                    value={advancedSettings.cpuInactivityTimeoutAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>CPU Inactivity Timeout (DC)</InputLabel>
                                <Select
                                    label="CPU Inactivity Timeout (DC)"
                                    name="cpuInactivityTimeoutDC"
                                    value={advancedSettings.cpuInactivityTimeoutDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {timeIntervals.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ border: `3px solid ${theme.palette.secondary.main}` }} />
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>USB Sleep (AC)</InputLabel>
                                <Select
                                    label="USB Sleep (AC)"
                                    name="usbSleepAC"
                                    value={advancedSettings.usbSleepAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {availableOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%' }}>
                                <InputLabel>USB Sleep (DC)</InputLabel>
                                <Select
                                    label="USB Sleep (DC)"
                                    name="usbSleepDC"
                                    value={advancedSettings.usbSleepDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {availableOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>Wake Timers (AC)</InputLabel>
                                <Select
                                    label="Wake Timers (AC)"
                                    name="wakeTimersAC"
                                    value={advancedSettings.wakeTimersAC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {availableOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>Wake Timers (DC)</InputLabel>
                                <Select
                                    label="Wake Timers (DC)"
                                    name="wakeTimersDC"
                                    value={advancedSettings.wakeTimersDC}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {availableOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>HID Wake</InputLabel>
                                <Select
                                    label="HID Wake"
                                    name="hidWake"
                                    value={advancedSettings.hidWake}
                                    onChange={handleChangeAdvancedSettings}
                                >
                                    {availableOptions.map((data, key) => (
                                        <MenuItem key={key} value={data.value}>
                                            {data.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="cpuMax">CPU Max</InputLabel>
                                <OutlinedInput
                                    id="cpuMax"
                                    name="cpuMax"
                                    type="number"
                                    label="CPU Max"
                                    value={advancedSettings.cpuMax}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                    // aria-describedby="outlined-weight-helper-text"
                                    // inputProps={{
                                    //     'aria-label': ''
                                    // }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="diskMax">Disk Max</InputLabel>
                                <OutlinedInput
                                    id="diskMax"
                                    name="diskMax"
                                    type="number"
                                    label="Disk Max"
                                    value={advancedSettings.diskMax}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="networkMax">Network Max</InputLabel>
                                <OutlinedInput
                                    id="networkMax"
                                    name="networkMax"
                                    type="number"
                                    label="Network Max"
                                    value={advancedSettings.networkMax}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">pkt/s</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="screenDim">Screen Dim</InputLabel>
                                <OutlinedInput
                                    id="screenDim"
                                    name="screenDim"
                                    type="number"
                                    label="Screen Dim"
                                    value={advancedSettings.screenDim}
                                    onChange={handleChangeAdvancedSettings}
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel htmlFor="brightness">Brightness</InputLabel>
                                <OutlinedInput
                                    id="brightness"
                                    name="brightness"
                                    type="number"
                                    label="Brightness"
                                    value={advancedSettings.brightness}
                                    onChange={handleChangeAdvancedSettings}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    disabled={
                        name === '' ||
                        startTime === '' ||
                        endTime === '' ||
                        advancedSettings.cpuMax === '' ||
                        advancedSettings.diskMax === '' ||
                        advancedSettings.networkMax === '' ||
                        advancedSettings.screenDim === '' ||
                        advancedSettings.brightness === ''
                    }
                    onClick={handleSubmit}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

EditSchemeDialog.propTypes = {
    onClose: PropTypes.func,
    data: PropTypes.object,
    index: PropTypes.number
};

export default EditSchemeDialog;
